import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RoutePaths } from '@shared/enums/routes.enums';
import { CallSubType, CallType, GenesysData } from '@shared/interfaces/CallLog';
import { StorageApiService } from '@shared/services/storage-api.service';
import { TabControllerService } from '@shared/services/tab-controller.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { GenesysBridgeService } from './services/genesys-bridge.service';
import { GenesysContactFinder } from './services/genesys-contact-finder.service';
import { GenesysFacadeService } from './services/genesys-facade.service';
import { GenesysTelephonyService } from './services/genesys-telephony.service';

@Component({
  selector: 'horizon-genesys-telephony',
  templateUrl: './horizon-genesys-telephony.component.html',
  styleUrls: ['./horizon-genesys-telephony.component.scss'],
})
export class HorizonGenesysTelephonyComponent implements OnInit, OnDestroy {
  @Input() showGenesysTelephony = false;
  @Input() showIncomingCallNotification = false;

  genesysTelephonyUrl: SafeResourceUrl = null;
  url =
    'https://apps.euw2.pure.cloud/crm/embeddableFramework.html?dedicatedLoginWindow=true';
  interactionId = '';
  phoneIcon$ = this.genesysFacadeService.icon$;
  private genesysData: GenesysData = {
    callID: '',
    phoneNumber: '',
    callType: null,
    callSubType: null,
  };
  private destroy$ = new Subject<void>();

  constructor(
    private readonly domSanitizer: DomSanitizer,
    private readonly genesysTelephonyService: GenesysTelephonyService,
    private readonly genesysBridgeService: GenesysBridgeService,
    private readonly genesysFacadeService: GenesysFacadeService,
    private readonly tabControllerService: TabControllerService,
    private readonly storageApiService: StorageApiService,
    private readonly genesysContactFinder: GenesysContactFinder
  ) {}

  ngOnInit(): void {
    this.genesysTelephonyUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
      this.url
    );
    this.genesysBridgeService.interactionSubscription$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        this.handleInteractionEvent(message.data);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleInteractionEvent(messageData: any) {
    const handler = this.handlerInteractionEvent[messageData.category];
    if (handler) {
      return handler(messageData.interaction);
    } else {
      console.warn(
        "Couldn't find handler for interaction event:",
        messageData.category
      );
      return null;
    }
  }

  private handlerInteractionEvent = {
    connect: (interaction: any) => this.handleConnectInteraction(interaction),
    disconnect: (interaction: any) =>
      this.handleDisconnectInteraction(interaction),
  };
  private handleConnectInteraction(interaction: any): void {
    const state = this.checkInteractionState(interaction);
    this.setGenesysTelephonyIcon(state);
    if ((interaction.calledNumber ?? '') !== '')
      this.handleCallDirection(interaction);
  }

  private setGenesysTelephonyIcon(state: string): void {
    if (state === 'ALERTING') {
      this.genesysTelephonyService.setGenesysTelephonyIcon('incoming');
      if (!this.showGenesysTelephony) {
        this.genesysTelephonyService.showIncomingCallNotification();
      }
    }
  }

  private handleCallDirection(interaction: any): void {
    this.genesysData.callID = interaction.id;
    if (interaction.direction === 'Inbound') {
      this.startInboundCall({ data: { interaction } });
    } else {
      this.startOutboundCall({ data: { interaction } });
    }
  }

  private checkInteractionState(interaction: any): string {
    let state;
    if (interaction.old) {
      state = interaction.old.state;
      this.interactionId = interaction.old.id;
    } else {
      state = interaction.state;
      this.interactionId = interaction.id;
    }
    return state;
  }

  private handleDisconnectInteraction(interaction: any): void {
    if (interaction.direction === 'Outbound' && !this.genesysData.callID) {
      this.genesysData.callID = interaction.id;
      this.startOutboundCall({ data: { interaction } });
    } else this.genesysTelephonyService.setGenesysTelephonyIcon('selected');
  }

  public answerCall(): void {
    this.genesysBridgeService.callInteraction('pickup', this.interactionId);
    this.genesysTelephonyService.hideIncomingCallNotification();
    this.genesysTelephonyService.showGenesysTelephony();
  }

  public declineCall(): void {
    this.genesysBridgeService.callInteraction('disconnect', this.interactionId);
    this.genesysTelephonyService.setGenesysTelephonyIcon('default');
    this.genesysTelephonyService.hideIncomingCallNotification();
  }

  private startOutboundCall(args: any): void {
    this.genesysData = this.genesysTelephonyService.buildGenesysInteractionData(
      args.data.interaction
    );

    if (this.genesysData.callSubType === CallSubType.Dialler) {
      this.listenForContact();
      return;
    }

    this.tabController();
  }

  private listenForContact() {
    this.genesysContactFinder
      .getContactDetail()
      .pipe(filter((contact) => !!contact))
      .subscribe((contact) => {
        this.genesysData = {
          ...this.genesysData,
          accountNumber: contact.accountNumber,
        };
        this.tabController();
      });
  }

  private startInboundCall(args: any): void {
    this.genesysData.phoneNumber = args.data.interaction.ani;
    this.genesysData.callType = CallType.Inbound;
    this.tabController();
  }

  private tabController(): void {
    this.storageApiService.setLocalStorageData('genesysData', this.genesysData);
    if (!this.tabControllerService.isTabOpen('genesysGeneratedTab'))
      this.tabControllerService.openTab(
        'genesysGeneratedTab',
        RoutePaths.SEARCH
      );
  }
}
